import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "./utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative overflow-hidden group",
  {
    variants: {
      variant: {
        default: "bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 text-white hover:from-orange-600 hover:via-orange-700 hover:to-amber-700 hover:text-white shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 [&>*]:text-white hover:[&>*]:text-white",
        destructive:
          "bg-gradient-to-r from-red-500 via-red-600 to-rose-600 text-white shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 hover:scale-[1.02] active:scale-[0.98] before:absolute before:inset-0 before:bg-gradient-to-r before:from-red-400 before:via-red-500 before:to-rose-500 before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300",
        outline:
          "border-2 border-orange-300 dark:border-orange-700 bg-background/80 backdrop-blur-sm text-foreground hover:text-foreground hover:bg-gradient-to-r hover:from-orange-50/50 hover:to-amber-50/50 dark:hover:from-orange-950/30 dark:hover:to-amber-950/30 hover:border-orange-400 dark:hover:border-orange-600 hover:shadow-lg hover:shadow-orange-200/30 dark:hover:shadow-orange-900/20 hover:scale-[1.02] active:scale-[0.98] transition-all [&>*]:relative [&>*]:z-10",
        secondary:
          "bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-950/50 dark:to-amber-950/50 text-orange-900 dark:text-orange-100 border border-orange-200 dark:border-orange-800 hover:from-orange-200 hover:to-amber-200 dark:hover:from-orange-900/70 dark:hover:to-amber-900/70 hover:shadow-md hover:shadow-orange-200/20 dark:hover:shadow-orange-900/10 hover:scale-[1.02] active:scale-[0.98]",
        ghost: "text-foreground hover:text-foreground hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 dark:hover:from-orange-950/30 dark:hover:to-amber-950/30 hover:shadow-sm hover:scale-[1.02] active:scale-[0.98]",
        link: "text-orange-600 dark:text-orange-400 underline-offset-4 hover:underline hover:text-orange-700 dark:hover:text-orange-300 transition-colors",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

